import React, { useState } from "react";
import { Button, Card, Col, Container, Form, Modal, Row, Table } from "react-bootstrap";
import { 
  useGetSubscriptionPlanLimitsQuery, 
  useCreateOrUpdatePlanLimitMutation,
  useDeletePlanLimitMutation 
} from "../../../store/reducers/adminApi";

interface PlanLimit {
  price_id: string;
  plan_name: string;
  session_limit: number;
  session_minutes: number;
}

const SubscriptionLimits: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingLimit, setEditingLimit] = useState<PlanLimit | null>(null);
  const [formData, setFormData] = useState<PlanLimit>({
    price_id: "",
    plan_name: "",
    session_limit: 1,
    session_minutes: 60
  });

  const { data: limitsData, isLoading, refetch } = useGetSubscriptionPlanLimitsQuery();
  const [createOrUpdateLimit] = useCreateOrUpdatePlanLimitMutation();
  const [deleteLimit] = useDeletePlanLimitMutation();

  const limits = limitsData?.limits || [];
  
  // Debug log to see what data we're getting
  console.log('Subscription limits data:', limitsData);
  console.log('Limits array:', limits);

  const handleShowModal = (limit?: PlanLimit) => {
    if (limit) {
      setEditingLimit(limit);
      setFormData(limit);
    } else {
      setEditingLimit(null);
      setFormData({
        price_id: "",
        plan_name: "",
        session_limit: 1,
        session_minutes: 60
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLimit(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createOrUpdateLimit(formData).unwrap();
      handleCloseModal();
      refetch();
      alert(editingLimit ? "Plan limit updated successfully" : "Plan limit created successfully");
    } catch (error) {
      console.error("Failed to save plan limit:", error);
      alert("Failed to save plan limit");
    }
  };

  const handleDelete = async (priceId: string) => {
    if (window.confirm("Are you sure you want to delete this plan limit?")) {
      try {
        await deleteLimit(priceId).unwrap();
        refetch();
        alert("Plan limit deleted successfully");
      } catch (error) {
        console.error("Failed to delete plan limit:", error);
        alert("Failed to delete plan limit");
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "number" ? parseInt(value) || 0 : value
    }));
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h2>Subscription Plan Limits</h2>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => handleShowModal()}>
            Add Plan Limit
          </Button>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          {isLoading ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Price ID</th>
                  <th>Plan Name</th>
                  <th>Session Limit</th>
                  <th>Session Minutes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {limits.map((limit: PlanLimit) => (
                  <tr key={limit.price_id}>
                    <td>{limit.price_id}</td>
                    <td>{limit.plan_name}</td>
                    <td>{limit.session_limit}</td>
                    <td>{limit.session_minutes} min</td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleShowModal(limit)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(limit.price_id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
                {limits.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-4">
                      No plan limits configured. Add one to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingLimit ? "Edit Plan Limit" : "Add Plan Limit"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Price ID (Stripe)</Form.Label>
              <Form.Control
                type="text"
                name="price_id"
                value={formData.price_id}
                onChange={handleInputChange}
                placeholder="e.g., price_1234567890"
                required
                disabled={!!editingLimit}
              />
              <Form.Text className="text-muted">
                The Stripe price ID for this subscription plan
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Plan Name</Form.Label>
              <Form.Control
                type="text"
                name="plan_name"
                value={formData.plan_name}
                onChange={handleInputChange}
                placeholder="e.g., Premium Monthly"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Session Limit</Form.Label>
              <Form.Control
                type="number"
                name="session_limit"
                value={formData.session_limit}
                onChange={handleInputChange}
                min="1"
                max="100"
                required
              />
              <Form.Text className="text-muted">
                Number of sessions allowed per billing period
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Session Minutes</Form.Label>
              <Form.Control
                type="number"
                name="session_minutes"
                value={formData.session_minutes}
                onChange={handleInputChange}
                min="1"
                max="120"
                required
              />
              <Form.Text className="text-muted">
                Maximum minutes per session
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingLimit ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default SubscriptionLimits;